// One-click staffing plan: production-qual crews first (the hard prodQualMinImpl
// gate — anything less draws ZERO and the qual stalls), then the WHOLE remaining
// pool is split across every staffable project in proportion to its ideal team,
// largest-remainder rounded to whole heads. Surplus overstaffs past ideal rather
// than idling: payroll is owed either way and effectiveHeads never decreases, so
// an idle head is strictly worse than an overflow-band head — and equal
// n/ideal ratios maximize total effective heads on both sides of the ideal.
// Availability mirrors the engine's draw priority via projectNextMonth: NRE →
// sustaining → node qual/research/reliability → tool programs are reserved
// before any project sees a head, and queued orders (decisions, NRE, program
// dials) are applied.

import type { Command, ContentPack, Discipline, PlayerView, ProjectPhase, ViewProject } from "../engine/types.js";
import { DISCIPLINES } from "../engine/types.js";
import { MAX_STAFF_PER_PROJECT } from "../engine/commands.js";
import { idealStaffing } from "../engine/formulas/effort.js";
import { projectNextMonth } from "./allocation.js";

/** The phase a project is HEADED to with its queued decision applied — the same
 *  mapping as projectNextMonth and the Pipeline card dials (keep them in step). */
export function effectivePhase(p: ViewProject, pending: readonly Command[]): ProjectPhase {
  const c = pending.find(
    (x) =>
      (x.type === "tapeIn" || x.type === "tapeOut" || x.type === "respin" || x.type === "cancelProject") &&
      x.projectId === p.id,
  );
  if (!c) return p.phase;
  if (c.type === "tapeIn" && p.phase === "waitingTapein") return "layout";
  if (c.type === "tapeOut" && p.phase === "waitingTapeout") return "fab";
  if (c.type === "respin" && p.phase === "respinDecision") return "respinFix";
  if (c.type === "cancelProject") return "cancelled";
  return p.phase;
}

/** Phases where the engine draws project staff next month. Everything else
 *  (fab, validation, respinDecision, cancelled) is left untouched — the engine
 *  already cleared or ignores staffing there. */
const STAFFABLE: readonly ProjectPhase[] = [
  "feasibility",
  "design",
  "waitingTapein",
  "layout",
  "waitingTapeout",
  "respinFix",
  "prodQual",
];

/** setStaffing orders that move every staffable project to the auto plan.
 *  Empty when the plan matches what's already set/queued. */
export function autoStaffCommands(
  view: PlayerView,
  pending: readonly Command[],
  content: ContentPack,
): Extract<Command, { type: "setStaffing" }>[] {
  const models = content.economy.models;
  const proj = projectNextMonth(view, pending, content);

  // whole heads only (steppers are integers); fractional ramping credit stays idle
  const avail: Record<Discipline, number> = { design: 0, verification: 0, implementation: 0 };
  for (const d of DISCIPLINES) {
    const x = proj[d];
    avail[d] = Math.max(0, Math.floor(x.budget - x.nre - x.sustaining - x.qual - x.research - x.reliability - x.tools - x.tree + 1e-9));
  }

  const entries = view.projects
    .map((p) => ({ p, phase: effectivePhase(p, pending) }))
    .filter((e) => STAFFABLE.includes(e.phase))
    .map((e) => ({ ...e, ideal: idealStaffing({ phase: e.phase, blocks: e.p.blocks }, models) }));

  const plan = new Map<string, Record<Discipline, number>>();
  for (const e of entries) plan.set(e.p.id, { design: 0, verification: 0, implementation: 0 });

  // 1) qual first — a full crew or none: below the gate the engine draws nothing,
  //    so partial staffing would only starve projects that can use the heads
  for (const e of entries) {
    if (e.phase !== "prodQual") continue;
    if (avail.implementation >= models.prodQualMinImpl) {
      plan.get(e.p.id)!.implementation = models.prodQualMinImpl;
      avail.implementation -= models.prodQualMinImpl;
    }
  }

  // 2) everyone else — the full pool, proportional to ideal (scale > 1 = overstaff),
  //    leftover whole heads by largest remainder. Disciplines nobody can use
  //    (every ideal 0) stay idle: the engine banks nothing for them anywhere.
  for (const d of DISCIPLINES) {
    const cands = entries.filter((e) => e.phase !== "prodQual" && e.ideal[d] > 0);
    const idealSum = cands.reduce((a, e) => a + e.ideal[d], 0);
    if (cands.length === 0 || idealSum <= 0) continue;
    const scale = avail[d] / idealSum;
    let floorSum = 0;
    let quotaSum = 0;
    const rows = cands.map((e) => {
      const quota = e.ideal[d] * scale;
      const base = Math.floor(quota + 1e-9);
      floorSum += base;
      quotaSum += quota;
      return { e, quota, base };
    });
    let units = Math.min(avail[d] - floorSum, Math.round(quotaSum - floorSum));
    for (const r of [...rows].sort((a, b) => b.quota - b.base - (a.quota - a.base))) {
      if (units <= 0) break;
      r.base += 1;
      units -= 1;
    }
    for (const r of rows) plan.get(r.e.p.id)![d] = Math.min(MAX_STAFF_PER_PROJECT, r.base);
  }

  // 3) emit only the diffs vs. what's already set (or queued — queue is replace-keyed)
  const commands: Extract<Command, { type: "setStaffing" }>[] = [];
  for (const e of entries) {
    const target = plan.get(e.p.id)!;
    const queued = pending.find((c) => c.type === "setStaffing" && c.projectId === e.p.id);
    const cur = queued && queued.type === "setStaffing" ? queued.staffing : e.p.staffing;
    if (DISCIPLINES.some((d) => (cur[d] ?? 0) !== target[d])) {
      commands.push({ type: "setStaffing", projectId: e.p.id, staffing: { ...target } });
    }
  }
  return commands;
}
